/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Globe, Briefcase, Bot, Lock, Smartphone, Repeat } from 'lucide-react';

export const UseCasesDoc = () => (
  <div className="space-y-12 animate-fade-in">
    <section>
        <p className="text-lg text-stone-600 dark:text-stone-300 leading-relaxed font-medium">
            GhostPay's architecture enables a wide range of financial applications, from everyday retail to complex automated machine economies.
        </p>
    </section>

    <div className="space-y-8">
        {/* Use Case 1 */}
        <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 hover:shadow-lg transition-shadow">
            <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Globe size={28} />
                </div>
            </div>
            <div>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-2">Cross-Border E-Commerce</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                    A merchant in Germany sells bespoke furniture to a client in Japan. Traditional rails would incur 3-5% FX fees and take days to settle. With GhostPay:
                </p>
                <ul className="grid sm:grid-cols-2 gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Customer pays in USDC (Solana).</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Merchant receives EURC (Euro Stablecoin) instantly.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Total fee: 0.1% protocol fee + ~0.0002 SOL gas.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Zero chargeback risk.</li>
                </ul>
            </div>
        </div>

        {/* Use Case 2 */}
        <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 hover:shadow-lg transition-shadow">
            <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Bot size={28} />
                </div>
            </div>
            <div>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-2">AI Agent Economy (x402)</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                    An autonomous research agent needs to purchase a dataset from a gated API. It holds a wallet but no credit card.
                </p>
                <ul className="grid sm:grid-cols-2 gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Agent hits API endpoint.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> API returns 402 Payment Required header with price.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Agent signs & broadcasts transaction instantly.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> GhostPay validates tx and issues API access token.</li>
                </ul>
            </div>
        </div>

        {/* Use Case 3 */}
        <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 hover:shadow-lg transition-shadow">
            <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Briefcase size={28} />
                </div>
            </div>
            <div>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-2">Freelance Escrow</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                    A developer is hired for a 3-month project. Trust is established via a smart contract rather than a middleman platform taking 20%.
                </p>
                <ul className="grid sm:grid-cols-2 gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Client deposits full project amount into Escrow.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Funds are visible on-chain but locked.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Milestone 1 complete: Client signs release.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Funds stream directly to developer's wallet.</li>
                </ul>
            </div>
        </div>

        {/* Use Case 4 */}
        <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-white/[0.02] rounded-2xl border border-stone-200 dark:border-white/10 hover:shadow-lg transition-shadow">
            <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-stone-100 dark:bg-white/10 rounded-xl flex items-center justify-center text-stone-900 dark:text-white">
                    <Lock size={28} />
                </div>
            </div>
            <div>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-white mb-2">Token-Gated Retail</h3>
                <p className="text-sm text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                    A luxury streetwear brand releases a limited edition hoodie available only to holders of their NFT collection.
                </p>
                <ul className="grid sm:grid-cols-2 gap-2 text-xs text-stone-500 dark:text-stone-400">
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> User connects wallet to Ghost Storefront.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> GhostOS verifies NFT ownership on-chain.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> "Buy Now" button unlocks.</li>
                    <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-nobel-gold rounded-full"/> Purchase executes, updating physical inventory.</li>
                </ul>
            </div>
        </div>
    </div>
  </div>
);